import React from 'react';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Cell,
} from 'recharts';
import { cn } from '../../lib/utils';
import { borderPrimary, cardClass, largeTextSize, primaryText, regularText, semiBoldText, smallTextSize } from '../../styling/stylingHelper';

export interface BarDataPoint {
  name: string;
  value: number;
  color: string;
}

interface BarChartCardProps {
  title: string;
  data: BarDataPoint[];
  currency?: string;
  height?: number;
  className?: string;
  valueLabel?: string;
  maxBarWidth?: number;
  hideXAxisLabels?: boolean;
}

// Smart number formatting function with decimals param
const formatNumber = (
  value: number,
  currency?: string,
  decimals: number = 1
): string => {
  const absValue = Math.abs(value);
  let formatted = '';

  if (absValue >= 1000000) {
    formatted = `${(value / 1000000).toFixed(decimals)}M`;
  } else if (absValue >= 1000) {
    formatted = `${(value / 1000).toFixed(decimals)}k`;
  } else {
    formatted = value.toFixed(decimals);
  }

  return currency ? `${currency}${formatted}` : formatted;
};

// Smart tick formatter that prevents duplicate labels
const formatTickSmart = (
  value: number,
  allTicks: number[],
  currency: string = '€'
): string => {
  const hasThousandOrMore = allTicks.some(v => Math.abs(v) >= 1000);

  // If any tick is ≥1000, switch EVERYONE to "k" notation with 1 decimal where it makes sense
  if (hasThousandOrMore) {
    const absValue = Math.abs(value);

    if (absValue < 1000) {
      // Below 1000 → show normal with 0 decimals (keeps chart clean)
      return `${currency}${absValue.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
    }

    // ≥1000 → show in thousands with exactly 1 decimal (e.g. 1256 → 1.3k)
    const inThousands = absValue / 1000;
    const formatted = inThousands.toFixed(1).replace(/\.0$/, ''); // removes .0 → 1k instead of 1.0k if whole

    // Optional: you can force .0 if you prefer consistency: remove the .replace above
    return `${currency}${formatted}k`;
  }

  // No value ≥1000 → just format normally with 0 decimals
  return `${currency}${Math.abs(value).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
};


// Tooltip values use 1 decimal
const formatTooltip = (value: number, currency?: string) => {
  return formatNumber(value, currency, 2);
};

function getEightNiceTicks(min: number, max: number): number[] {
  // Always 8 ticks, including min and max.
  if (min === max) {
    // For edge case: only one value
    if (min === 0) {
      // All data zero
      return [0, 0.125, 0.25, 0.375, 0.5, 0.625, 0.75, 0.875, 1];
    } else {
      // All values same but not 0
      const up = min * 1.2;
      return [min, min + (up - min) * 0.125, min + (up - min) * 0.25, min + (up - min) * 0.375, min + (up - min) * 0.5, min + (up - min) * 0.625, min + (up - min) * 0.75, min + (up - min) * 0.875, up];
    }
  }
  const step = (max - min) / 7;
  return Array.from({ length: 8 }, (_, i) => min + step * i);
}

// Utility to measure the width of a text using a canvas context
const measureTextWidth = (text: string, font: string): number => {
  if (typeof window === "undefined") return 60; // default for SSR or non-browser
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return 60;
  ctx.font = font;
  return ctx.measureText(text).width;
};

export const BarChartCard: React.FC<BarChartCardProps> = ({
  title,
  data,
  currency = '€',
  height = 400,
  className,
  valueLabel = 'Spent',
  maxBarWidth = 150,
  hideXAxisLabels = false,
}) => {
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const [hoveredBar, setHoveredBar] = React.useState<{ index: number; x: number; y: number } | null>(null);

  // Calculate Y axis domain: always start at 0, go 20% higher than the highest value
  let maxY = 0;
  data.forEach((d) => {
    if (typeof d.value === 'number' && !isNaN(d.value)) {
      if (d.value > maxY) maxY = d.value;
    }
  });

  // Find the minY value
  let minY = 0;
  let foundMin = false;
  data.forEach((d) => {
    if (typeof d.value === 'number' && !isNaN(d.value)) {
      if (!foundMin || d.value < minY) {
        minY = d.value;
        foundMin = true;
      }
    }
  });
  // Clamp to 0 for min if all values >= 0
  minY = minY < 0 ? minY : 0;

  // max with headroom
  const yMax = maxY * 1.2;
  const yMin = minY;

  // Generate 8 ticks
  const yTicks = getEightNiceTicks(yMin, yMax === 0 ? 1 : yMax);

  // Set domain from ticks
  const yDomain: [number, number] = [yTicks[0], yTicks[7]];

  // --------- DYNAMIC Y-AXIS WIDTH LOGIC BEGIN -----------
  const fontSize = 13;
  const fontFamily = 'Inter, ui-sans-serif, system-ui, sans-serif';
  const fontString = `400 ${fontSize}px ${fontFamily}`;
  const yLabels = yTicks.map((v) => formatTickSmart(v, yTicks, currency));
  let maxWidth = 0;
  yLabels.forEach(label => {
    const w = measureTextWidth(label, fontString);
    if (w > maxWidth) {
      maxWidth = w;
    }
  });

  const axisWidth = Math.ceil(maxWidth + 24);
  // --------- DYNAMIC Y-AXIS WIDTH LOGIC END -------------

  const CustomTooltip = ({ active, payload, label, coordinate }: any) => {
    if (active && payload && payload.length && hoveredBar !== null) {
      // Position tooltip centered above the bar
      return (
        <div
          className={cn("border", borderPrimary, cardClass, "p-3")}
          style={{
            position: 'absolute',
            left: `${hoveredBar.x}px`,
            top: `${hoveredBar.y - 10}px`,
            transform: 'translate(-50%, -100%)',
            pointerEvents: 'none',
            zIndex: 1000,
          }}
        >
          <p className={cn(smallTextSize, semiBoldText, primaryText, "mb-2")}>{label}</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between gap-4">
              <span className={cn(smallTextSize, regularText)} style={{ color: "#666D80" }}>{valueLabel}</span>
              <span
                className={cn(smallTextSize, semiBoldText)}
                style={{ color: entry.fill || '#000' }}
              >
                {formatTooltip(entry.value, currency)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  // Custom X-axis tick with green text by default, and just bold (not purple) on hover
  const CustomXAxisTick = (props: any) => {
    const { x, y, payload, index } = props;
    const isActive = activeIndex === index;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fill="#101828"
          fontSize={13}
          fontWeight={isActive ? 700 : 500}
        >
          {payload.value}
        </text>
      </g>
    );
  };

  return (
    <div className={cn('w-full', cardClass, className)}>
      <div className="flex justify-between items-center p-4">
        <h3 className={cn(primaryText, largeTextSize, semiBoldText)}>{title}</h3>
      </div>
      <div className="pb-2 pr-2">
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={data}
            margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
            maxBarSize={maxBarWidth}
            onMouseMove={(state: any) => {
              if (state && state.activeTooltipIndex !== undefined) {
                setActiveIndex(state.activeTooltipIndex);
                // Calculate bar center position for tooltip
                if (state.activeCoordinate) {
                  setHoveredBar({
                    index: state.activeTooltipIndex,
                    x: state.activeCoordinate.x,
                    y: state.activeCoordinate.y,
                  });
                }
              }
            }}
            onMouseLeave={() => {
              setActiveIndex(null);
              setHoveredBar(null);
            }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e5e7eb"
              vertical={false}
            />
            <XAxis
              dataKey="name"
              tick={hideXAxisLabels ? false : <CustomXAxisTick />}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
              interval={data.length > 12 ? Math.floor(data.length / 12) : 0}
              height={hideXAxisLabels ? 0 : 40}
            />
            <YAxis
              tickFormatter={(v: number) => formatTickSmart(v, yTicks, currency)}
              tick={{ fontSize: 13, fill: '#6b7280' }}
              tickLine={false}
              axisLine={false}
              width={axisWidth}
              domain={yDomain}
              ticks={yTicks}
            />
            <Tooltip content={<CustomTooltip />} cursor={false} />
            <Bar
              dataKey="value"
              radius={[10, 10, 0, 0]}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  opacity={activeIndex === null || activeIndex === index ? 1 : 1}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
